import { useContext, useState } from 'react';
import { useDialog } from '../../../../../common/dialog/useDialog';
import { ToastNotificationContext } from '../../../../../common/toast-notification';
import { BusinessError } from '../../../../../common/business-error';
import { WapAppEditData, WapAppFormData } from './interfaces';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import endpoints from '../../../../../../api/endpoints';
import { handleResponseError } from '../../../../../../api/handleResponseError';
import { getEditAppBusinessError } from './businessErrors';

export type DialogError = BusinessError | null;
export const useEditApp = () => {
  const [initEditFormData, setInitEditFormData] = useState<WapAppFormData>();
  const [isEditAppLoading, setEditAppLoading] = useState<boolean>(false);
  const {
    isDialogOpen: isMainDialogOpen,
    handleOpenDialog: handleOpenMainDialog,
    handleCloseDialog: handleCloseMainDialog
  } = useDialog();

  const [error, setError] = useState<DialogError>(null);
  const { openToast } = useContext(ToastNotificationContext);

  const handleOpenEditAppDialog = (initWapAppSettings: WapAppFormData) => {
    setError(null);
    setInitEditFormData(initWapAppSettings);
    handleOpenMainDialog();
  };

  const handleCloseEditAppDialog = () => {
    setError(null);
    handleCloseMainDialog();
  };

  const handleSubmitEditAppDialog = async (wapAppEditFormData: WapAppEditData, onSuccess: () => void) => {
    if (wapAppEditFormData) {
      setError(null);
      setEditAppLoading(true);

      try {
        const response = await fetchWithTokenRefresh(
          endpoints.editWapAppSettings(wapAppEditFormData.appId),
          addParams('PUT', wapAppEditFormData)
        );

        if (response.status !== 204) {
          await handleResponseError(response);
        }

        onSuccess();
        setEditAppLoading(false);
        handleCloseEditAppDialog();
        openToast({
          message: `${wapAppEditFormData.appId} app edited successfully`,
          severity: 'success'
        });
      } catch (e) {
        const { message, severity } = getEditAppBusinessError(e);

        setError({ message, severity });
        setEditAppLoading(false);
      }
    }
  };

  return {
    error,
    initEditFormData,
    isEditAppDialogOpen: isMainDialogOpen,
    isEditAppLoading,
    handleOpenEditAppDialog,
    handleCloseEditAppDialog,
    handleSubmitEditAppDialog
  };
};
